import { FC, useState } from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { Link } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { useVulnerabilityListQuery, VulnerabilityListFieldsFragment } from "app/api/graph/types";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { vulnerabilityRoute, deleteVulnerabilityRoute, editVulnerabilityRoute } from "app/route/Routes";
import { SortableTableHeader, SortDirection } from "app/component/table/SortableTableHeader";
import { formatISODateTime } from "app/util/format";
import {
    VulnerabilitiesTableFiltersForm,
    FormValues,
} from "app/component/vulnerability/VulnerabilitiesTableFiltersForm";
import { BlockButtonGroup } from "app/component/button/BlockButtonGroup";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";

type TableRowProps = {
    vulnerability: VulnerabilityListFieldsFragment;
};

const TableRow = ({ vulnerability }: TableRowProps) => {
    const permissions = useGeneralPermissions(vulnerability.permissions);

    return (
        <tr>
            <td>
                <Link to={vulnerabilityRoute.build({ vulnerabilityId: vulnerability._id })}>
                    {vulnerability.identifier}
                </Link>
            </td>
            <td>{vulnerability.title}</td>
            <td>{formatISODateTime(vulnerability.createdAt)}</td>
            <td>
                {permissions.canUpdate ? (
                    <ButtonGroup>
                        <ButtonLink
                            to={editVulnerabilityRoute.build({
                                vulnerabilityId: vulnerability._id,
                            })}
                            variant="warning"
                            size="sm"
                            title="edit"
                        >
                            <FaEdit />
                        </ButtonLink>
                    </ButtonGroup>
                ) : null}
                {permissions.canDelete ? (
                    <ButtonGroup>
                        <ButtonLink
                            to={deleteVulnerabilityRoute.build({
                                vulnerabilityId: vulnerability._id,
                            })}
                            variant="danger"
                            size="sm"
                            title="Delete"
                        >
                            <FaTrash />
                        </ButtonLink>
                    </ButtonGroup>
                ) : null}
            </td>
        </tr>
    );
};

export const VulnerabilitiesTable: FC = () => {
    const [identifierOrder, setIdentifierOrder] = useState<SortDirection>("ASC");
    const [titleOrder, setTitleOrder] = useState<SortDirection>(undefined);
    const [createdAtOrder, setCreatedAtOrder] = useState<SortDirection>(undefined);
    const [filters, setFilters] = useState<FormValues>({
        identifier: undefined,
        title: undefined,
        testingTypes: undefined,
    });
    const { data, loading, error, fetchMore } = useVulnerabilityListQuery({
        variables: { order: { identifier: identifierOrder, title: titleOrder, createdAt: createdAtOrder }, ...filters },
        notifyOnNetworkStatusChange: true,
    });
    const vulnerabilities = data?.vulnerabilities;

    if (loading && !vulnerabilities) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!vulnerabilities) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const fetchMoreVulnerabilities = () => fetchMore({ variables: { after: vulnerabilities.pageInfo.endCursor } });

    return (
        <Row>
            <Col>
                <Table responsive="sm" bordered className="crud-table">
                    <thead>
                        <tr>
                            <SortableTableHeader direction={identifierOrder} onChange={setIdentifierOrder}>
                                Identifier
                            </SortableTableHeader>
                            <SortableTableHeader direction={titleOrder} onChange={setTitleOrder}>
                                Title
                            </SortableTableHeader>
                            <SortableTableHeader direction={createdAtOrder} onChange={setCreatedAtOrder}>
                                Created at
                            </SortableTableHeader>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mapNodes(vulnerabilities, (vulnerability) => (
                            <TableRow key={vulnerability.id} vulnerability={vulnerability} />
                        ))}
                        {vulnerabilities.edges?.length === 0 ? (
                            <tr>
                                <td colSpan={100}>No vulnerabilities.</td>
                            </tr>
                        ) : null}
                        {loading ? (
                            <tr>
                                <td colSpan={100}>
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        ) : null}
                        {vulnerabilities.pageInfo.hasNextPage ? (
                            <tr>
                                <td colSpan={100}>
                                    <BlockButtonGroup>
                                        <Button variant="light" onClick={fetchMoreVulnerabilities}>
                                            Load more
                                        </Button>
                                    </BlockButtonGroup>
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </Table>
            </Col>
            <Col md={3}>
                <VulnerabilitiesTableFiltersForm initialValues={filters} onSubmit={setFilters} />
            </Col>
        </Row>
    );
};
